'use client';

import Icon from '@/components/ui/AppIcon';

interface LegalComplianceSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function LegalComplianceSection({ isExpanded, onToggle }: LegalComplianceSectionProps) {
  const complianceAreas = [
    {
      title: 'Data Protection Regulations',
      icon: 'ShieldCheckIcon',
      description: 'Compliance with Indian and international data protection laws.',
      regulations: [
        'Information Technology Act, 2000 (India)',
        'Personal Data Protection Bill compliance',
        'GDPR compliance for European guests',
        'Data encryption and secure storage protocols',
        'Regular security audits and vulnerability assessments'
      ]
    },
    {
      title: 'Consumer Rights Under Indian Law',
      icon: 'ScaleIcon',
      description: 'Protection of consumer rights as per Indian legislation.',
      regulations: [
        'Consumer Protection Act, 2019 compliance',
        'Right to information about services and pricing',
        'Right to fair and transparent business practices',
        'Right to seek redressal for deficiency in services',
        'Protection against unfair trade practices'
      ]
    },
    {
      title: 'International Guest Protections',
      icon: 'GlobeAltIcon',
      description: 'Additional protections for international travelers.',
      regulations: [
        'Foreign Exchange Management Act (FEMA) compliance',
        'International payment gateway security standards',
        'Multi-currency pricing transparency',
        'Visa and immigration documentation support',
        'Consular notification procedures when required'
      ]
    }
  ];

  return (
    <section id="legal-compliance" className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="DocumentCheckIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Section 6</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Legal Compliance
            </h2>
            <p className="text-lg text-text-secondary">
              Information covering data protection regulations, consumer rights under Indian law, and international guest protections.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-premium border border-border overflow-hidden">
            <button
              onClick={onToggle}
              className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted transition-colors duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="ClipboardDocumentCheckIcon" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary">View Legal Compliance Information</h3>
                  <p className="text-sm text-text-secondary mt-1">Click to expand regulatory compliance details</p>
                </div>
              </div>
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={24} 
                className="text-text-secondary flex-shrink-0" 
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 space-y-6 animate-scale-in">
                {complianceAreas.map((area, index) => (
                  <div key={index} className="border-t border-border pt-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon name={area.icon as any} size={20} className="text-accent" />
                      </div>
                      <h4 className="text-lg font-semibold text-text-primary">{area.title}</h4>
                    </div>
                    <p className="text-text-secondary mb-4">{area.description}</p>
                    <ul className="space-y-2">
                      {area.regulations.map((regulation, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary">{regulation}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="border-t border-border pt-6">
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                    <div className="flex items-start space-x-3">
                      <Icon name="InformationCircleIcon" size={24} className="text-primary flex-shrink-0" />
                      <div>
                        <h5 className="font-semibold text-text-primary mb-2">Regulatory Authority</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          Proximus Serviced Apartments operates under the jurisdiction of Karnataka Tourism Department and complies with all applicable central and state regulations.
                        </p>
                        <p className="text-sm text-text-secondary">
                          <strong>License Number:</strong> KA/BLR/HSA/2024/12345<br />
                          <strong>GST Number:</strong> 29ABCDE1234F1Z5<br />
                          <strong>Tourism Registration:</strong> TRN/KA/2024/67890
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}